// "Komplettiere dein Set": adds the chosen plant variant plus the checked add-ons in one request and
// announces it with the same cart event the theme's own add-to-cart uses, so the drawer updates/opens.
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class VdCompleteSet extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.vd-set__add');
    this.status = this.querySelector('.vd-set__status');
    if (!(this.button instanceof HTMLButtonElement)) return;
    this.controller = new AbortController();
    this.button.addEventListener('click', () => this.add(), { signal: this.controller.signal });
    this.addEventListener('change', () => this.label(), { signal: this.controller.signal });
    this.label();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  /** The plant variant currently chosen in this product's own form. */
  mainLine() {
    const section = this.closest('.shopify-section') ?? document;
    const form = section.querySelector('product-form-component form, form[action*="/cart/add"]');
    const id = form?.querySelector('input[name="id"]');
    const qty = form?.querySelector('input[name="quantity"]');
    if (!(id instanceof HTMLInputElement) || !id.value) return null;
    const quantity = qty instanceof HTMLInputElement ? Math.max(1, Number(qty.value) || 1) : 1;
    return { id: Number(id.value), quantity };
  }

  addons() {
    return [...this.querySelectorAll('input[type="checkbox"]:checked')]
      .filter((i) => i instanceof HTMLInputElement)
      .map((i) => ({ id: Number(i.value), quantity: 1 }));
  }

  label() {
    const n = this.addons().length;
    const base = this.button.dataset.label || '';
    this.button.textContent = n === 0 ? 'Nur die Pflanze in den Warenkorb' : base;
  }

  async add() {
    const main = this.mainLine();
    if (!main) {
      this.say('Bitte wähle zuerst eine Variante der Pflanze.');
      return;
    }
    const items = [main, ...this.addons()];
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    this.button.setAttribute('aria-busy', 'true');
    this.button.disabled = true;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      const cart = await (await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`)).json();

      if (result.status) {
        this.dispatchEvent(new CartErrorEvent({ error: result.message || 'Add to cart failed', code: 'INVALID' }));
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items } });
        this.say(result.description || result.message || 'Das hat nicht geklappt. Bitte versuche es noch einmal.');
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'vd-complete-set',
          itemCount: items.reduce((s, i) => s + i.quantity, 0),
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });
      this.say(items.length > 1 ? `${items.length} Artikel im Warenkorb.` : 'Die Pflanze ist im Warenkorb.');
    } catch (error) {
      deferred.reject(error);
      this.say('Keine Verbindung. Bitte versuche es noch einmal.');
    } finally {
      this.button.removeAttribute('aria-busy');
      this.button.disabled = false;
    }
  }

  /** @param {string} text */
  say(text) {
    if (this.status) this.status.textContent = text;
  }
}

if (!customElements.get('vd-complete-set')) customElements.define('vd-complete-set', VdCompleteSet);
